import { Injectable } from '@angular/core';

import { Action, State, StateContext, Store } from '@ngxs/store';
import { append, insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { catchError, concat, mergeMap, Observable, tap, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { UiActions } from '@store/actions/ui.actions';

import { DeelnemersInitialState, DeelnemersStateModel } from '@store/models/deelnemers.model';
import { DeelnemersActions } from '@store/actions/deelnemers.actions';
import { OnderwijsLocatiesSelectors } from '@store/selectors/onderwijs-locaties.selectors';
import { AntwoordbladI, DeelnemerDTO, DeelnemerI, SchooladviesDTO } from '@models/deelnemer.model';
import { PlanningDTO, PlanningI } from '@models/planning.model';
import { ProductKey, StartCodeByProduct, StartcodeDTO, StartcodeI } from '@models/startcode.model';
import { ApiClientService } from '@services/api-client.service';

function flattenStartCodeByProduct(grouped: StartCodeByProduct | null, productKeys?: ProductKey[]): StartcodeDTO[] {
  if (!grouped) {return [];}
  const keys = productKeys?.length ? productKeys : Object.keys(grouped);
  const flat: StartcodeDTO[] = [];
  for (const product of keys) {
    if (grouped[product]) {
      flat.push(...grouped[product]);
    }
  }
  return flat;
}

@State<DeelnemersStateModel>({
  name: 'deelnemers',
  defaults: DeelnemersInitialState,
})
@Injectable()
export class DeelnemersState {
  constructor(
    private readonly apiDstService: ApiClientService,
    private translate: TranslateService,
    private store: Store,
  ) {}

  @Action(DeelnemersActions.loadRapportenAvailable)
  loadRapportenAvailable(ctx: StateContext<DeelnemersStateModel>) {
    const state = ctx.getState();
    const selectedOnderwijsLocatieId = this.store.selectSnapshot(OnderwijsLocatiesSelectors.selectedOnderwijsLocatieId);
    // TODO - add updatingRapporten to the state
    // ctx.patchState({ updatingRapporten: true, loaded: false });
    ctx.patchState({ loaded: false });

    return this.apiDstService.rapportenAvailable(selectedOnderwijsLocatieId, 'leerling').pipe(
      tap((response) => {
        if (response.body) {
          const available = response.body as string[];
          const existing = [...state.deelnemers];
          available.forEach((persoonId) => {
            const index = existing.findIndex((d) => d.persoonId === persoonId);

            if (index > -1) {
              existing[index] = { ...existing[index], rapport: { leerlingRapportAvailable: true } };
            }
          });
          ctx.patchState({ deelnemers: existing, loaded: true });
        } else {
          ctx.patchState({ loaded: true });
        }
      }),
      catchError(() => {
        // ctx.patchState({ loaded: true });
        this.store.dispatch(
          new UiActions.toasterShowError({
            summary: '',
            message: this.translate.instant('TOASTER_MESSAGES.RAPPORTEN_AVAILABLE_ERROR'),
          }),
        );
        return [];
      }),
    );
  }

  @Action(DeelnemersActions.putAntwoordbladNummers)
  putAntwoordbladenNummers(ctx: StateContext<DeelnemersStateModel>, action: DeelnemersActions.putAntwoordbladNummers) {
    const state = ctx.getState();
    // const deelnemers = action.payload;
    const deelnemerIds = action.payload.map((deelnemer) => deelnemer.persoonId);

    return this.apiDstService.putAntwoordbladNummers(deelnemerIds).pipe(
      tap((antwoordbladen) => {
        const awb = antwoordbladen as Array<AntwoordbladI & { persoonId: string }>;
        awb.forEach((antwoordblad) => {
          const index = state.deelnemers.findIndex((d) => d.persoonId === antwoordblad.persoonId);
          if (index > -1) {
            ctx.setState(
              patch<DeelnemersStateModel>({
                deelnemers: updateItem<DeelnemerI>(index, patch({ antwoordblad: antwoordblad })),
              }),
            );
          }
        });
      }),
      // catchError((err) =>
      //   this.store.dispatch(new UiActions.toasterShowError({
      //     summary: 'Antwoordbladen',
      //     message: this.translate.instant('TOASTER_MESSAGES.ANTWOORDBLADEN_DOWNLOAD_ERROR', { error: err.status }),
      //   }))
      // )
    );
  }

  @Action(DeelnemersActions.LoadDeelnemers)
  loadDeelnemers(ctx: StateContext<DeelnemersStateModel>, action: DeelnemersActions.LoadDeelnemers) {
    const state = ctx.getState();

    // Detect productKeys change BEFORE updating stored value
    const productKeysChanged = action.productKeys !== undefined &&
      JSON.stringify(action.productKeys) !== JSON.stringify(state.startcodeProductKeys);

    // Update stored productKeys when provided
    if (action.productKeys !== undefined) {
      ctx.patchState({ startcodeProductKeys: action.productKeys });
    }

    // Simple entity manager cache
    // Caching based on existing OnderwijsLocatieId in schoollocatieIds array
    const ids = [...state.deelnemersLocationIds];

    // disable cache by removing the OnderwijsLocatieId from the list
    if (action.forceReload) {
      ctx.patchState({ deelnemersLocationIds: [], cached: false });
      ids.splice(ids.indexOf(action.OnderwijsLocatieId), 1);
    }

    // Location cached and nothing changed → use cache
    if (ids.includes(action.OnderwijsLocatieId) && !action.forceReload && !productKeysChanged) {
      ctx.patchState({ loading: false, loaded: true, cached: true });
      return;
    }

    // Location cached but productKeys changed → clear stale startcodes and refresh for new product
    if (ids.includes(action.OnderwijsLocatieId) && !action.forceReload && productKeysChanged) {
      // Clear startcodes from the previous product context so stale codes are never shown.
      ctx.patchState({
        deelnemers: state.deelnemers.map(d => ({ ...d, startcode: null })),
        loading: true,
      });

      const startcodesByProduct = state.startcodesByProduct ?? {};
      const allCached = action.productKeys?.every(key => key in startcodesByProduct) ?? false;
      if (!allCached) {
        return ctx.dispatch(new DeelnemersActions.ReadDeelnemerStartcodes(action.productKeys));
      }
      // All cached — re-apply from cache so deelnemers show the correct product's startcodes
      const cachedStartcodes = flattenStartCodeByProduct(
        Object.fromEntries((action.productKeys ?? []).map(k => [k, startcodesByProduct[k] ?? []])),
      );
      this.applyStartCodes(ctx.getState(), ctx, cachedStartcodes);
      return;
    }

    // Location not cached (or force reload) → fetch deelnemers + startcodes
    ctx.patchState({ loading: true, loaded: false, cached: false, error: null });

    const deelnemers$: Observable<DeelnemerDTO[]> = this.apiDstService.getDeelnemersComplete(action.OnderwijsLocatieId);

    return deelnemers$.pipe(
      tap((deelnemers) => {
        const existing = [...state.deelnemers];
        deelnemers.forEach((deelnemer) => {
          const index = existing.findIndex((d) => d.persoonId === deelnemer.persoonId && d.onderwijslocatieId === action.OnderwijsLocatieId);
          const newDeelnemer = { ...deelnemer } as DeelnemerI;
          newDeelnemer.onderwijslocatieId = action.OnderwijsLocatieId;
          newDeelnemer.antwoordblad = null;
          newDeelnemer.rapport = null;
          newDeelnemer.startcode = null;

          if (index > -1) {
            existing[index] = newDeelnemer;
          } else {
            existing.push(newDeelnemer);
          }
        });

        const locationIds = state.deelnemersLocationIds.includes(action.OnderwijsLocatieId)
          ? state.deelnemersLocationIds
          : [...state.deelnemersLocationIds, action.OnderwijsLocatieId];

        ctx.patchState({ deelnemers: existing, deelnemersLocationIds: locationIds, loading: false });
      }),
      mergeMap((deelnemers) => {
        // Startcodes are embedded in the v1.1 deelnemers response, but we still dispatch
        // ReadDeelnemerStartcodes for fresh data + startcodesByProduct, waiting for it to
        // complete before signaling loaded.
        return concat(
          ctx.dispatch(new DeelnemersActions.ReadDeelnemerStartcodes(action.productKeys)),
          ctx.dispatch(new DeelnemersActions.LoadDeelnemersSuccess(deelnemers)),
        ).pipe(tap(() => ctx.patchState({ loaded: true })));
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.message });
        this.store.dispatch(
          new UiActions.toasterShowError({
            summary: '',
            message: this.translate.instant('TOASTER_MESSAGES.DEELNEMER_LOAD_ERROR', { error: error.status }),
          }),
        );
        return ctx.dispatch(new DeelnemersActions.LoadDeelnemersFailure(error));
      }),
    );
  }

  @Action(DeelnemersActions.SelectDeelnemer)
  selectDeelnemer(ctx: StateContext<DeelnemersStateModel>, action: DeelnemersActions.SelectDeelnemer) {
    ctx.patchState({ selectedDeelnemerId: action.payload.persoonId });
  }

  @Action(DeelnemersActions.SelectDeelnemerOnId)
  selectDeelnemerOnId(ctx: StateContext<DeelnemersStateModel>, action: DeelnemersActions.SelectDeelnemerOnId) {
    ctx.patchState({ selectedDeelnemerId: action.payload });
  }

  @Action(DeelnemersActions.DeselectDeelnemer)
  deselectDeelnemer(ctx: StateContext<DeelnemersStateModel>) {
    ctx.patchState({ selectedDeelnemerId: '' });
  }

  @Action(DeelnemersActions.CreateDeelnemer)
  createDeelnemer(ctx: StateContext<DeelnemersStateModel>, action: DeelnemersActions.CreateDeelnemer) {
    return this.apiDstService.createDeelnemer(action.onderwijsLocatieId, action.deelnemerInput).pipe(
      tap((deelnemer: DeelnemerDTO) => {
        const newDeelnemer = { ...deelnemer } as DeelnemerI;
        newDeelnemer.onderwijslocatieId = action.onderwijsLocatieId;
        newDeelnemer.planning = null;
        ctx.setState(
          patch<DeelnemersStateModel>({
            deelnemers: insertItem<DeelnemerI>(newDeelnemer, 0),
          }),
        );
        this.store.dispatch(new DeelnemersActions.SelectDeelnemer(newDeelnemer));
        this.store.dispatch(
          new UiActions.toasterShowSuccess({
            summary: newDeelnemer.displayNaam,
            message: this.translate.instant('TOASTER_MESSAGES.DEELNEMER_CREATE_SUCCESS'),
            testKey: 'deelnemer-toegevoegd',
          }),
        );
      }),
      catchError((err) => {
        this.store.dispatch(
          new UiActions.toasterShowError({
            summary: '',
            message: this.translate.instant('TOASTER_MESSAGES.DEELNEMER_CREATE_ERROR', { error: err.status }),
          }),
        );
        return throwError(() => err);
      }),
    );
  }

  @Action(DeelnemersActions.UpdateDeelnemer)
  updateDeelnemer(ctx: StateContext<DeelnemersStateModel>, action: DeelnemersActions.UpdateDeelnemer) {
    const state = ctx.getState();
    return this.apiDstService.updateDeelnemer(action.OnderwijsLocatieId, action.persoonId, action.deelnemerInput).pipe(
      tap((deelnemer) => {
        const returnedDeelnemer = deelnemer as DeelnemerI;
        // TODO - set onderwijslocatieId is important!
        // Better to have this ID from the backend!
        returnedDeelnemer.onderwijslocatieId = action.OnderwijsLocatieId;
        const index = state.deelnemers.findIndex((d) => d.persoonId === action.persoonId);
        if (index > -1) {
          ctx.setState(
            patch<DeelnemersStateModel>({
              deelnemers: updateItem<DeelnemerI>(index, returnedDeelnemer),
            }),
          );
          this.store.dispatch(
            new UiActions.toasterShowSuccess({
              summary: returnedDeelnemer.displayNaam,
              message: this.translate.instant('TOASTER_MESSAGES.DEELNEMER_UPDATE_SUCCESS'),
              testKey: 'deelnemer-opgeslagen',
            }),
          );
        }
      }),
      catchError((err) => {
        this.store.dispatch(
          new UiActions.toasterShowError({
            summary: '',
            message: this.translate.instant('TOASTER_MESSAGES.DEELNEMER_UPDATE_ERROR', { error: err.status }),
          }),
        );
        return throwError(() => err);
      }),
    );
  }

  @Action(DeelnemersActions.DeleteDeelnemer)
  deleteDeelnemer(ctx: StateContext<DeelnemersStateModel>, action: DeelnemersActions.DeleteDeelnemer) {
    return this.apiDstService.deleteDeelnemer(action.OnderwijsLocatieId, action.deelnemer.persoonId).pipe(
      tap(() => {
        ctx.setState(
          patch<DeelnemersStateModel>({
            deelnemers: removeItem<DeelnemerI>((deelnemer) => deelnemer.persoonId === action.deelnemer.persoonId),
          }),
        );
        this.store.dispatch(
          new UiActions.toasterShowSuccess({
            summary: action.deelnemer.displayNaam,
            message: this.translate.instant('TOASTER_MESSAGES.DEELNEMER_DELETE_SUCCESS'),
          }),
        );
      }),
      catchError((err) =>
        this.store.dispatch(
          new UiActions.toasterShowError({
            summary: action.deelnemer.displayNaam,
            message: this.translate.instant('TOASTER_MESSAGES.DEELNEMER_DELETE_ERROR', { error: err.status }),
          }),
        ),
      ),
    );
  }

  @Action(DeelnemersActions.UpdateSchooladvies)
  updateSchooladvies(ctx: StateContext<DeelnemersStateModel>, action: DeelnemersActions.UpdateSchooladvies) {
    const state = ctx.getState();

    return this.apiDstService
      .updateSchooladvies(action.OnderwijsLocatieId, action.persoonId, { schooladviesId: action.schooladviesId })
      .pipe(
        tap((res) => {
          const result = res as SchooladviesDTO;
          const index = state.deelnemers.findIndex((d) => d.persoonId === action.persoonId);

          if (index > -1) {
            this.store.dispatch(
              new UiActions.toasterShowSuccess({
                summary: state.deelnemers[index].displayNaam,
                message: this.translate.instant('TOASTER_MESSAGES.SCHOOLADVIES_UPDATE_SUCCESS'),
                testKey: 'voorlopig-schooladvies-opgeslagen',
              }),
            );

            ctx.setState(
              patch<DeelnemersStateModel>({
                deelnemers: updateItem<DeelnemerI>(index, patch({ schooladvies: result.schooladviesId })),
              }),
            );
          }
        }),
        catchError((err) =>
          this.store.dispatch(
            new UiActions.toasterShowError({
              summary: 'Schooladvies',
              message: this.translate.instant('TOASTER_MESSAGES.SCHOOLADVIES_UPDATE_ERROR', { error: err.status }),
            }),
          ),
        ),
      );
  }

  @Action(DeelnemersActions.AddSelectedDeelnemersId)
  addSelectedDeelnemersId(ctx: StateContext<DeelnemersStateModel>, action: DeelnemersActions.AddSelectedDeelnemersId) {
    const state = ctx.getState();
    const ids = state.selectedDeelnemersIds;

    if (!ids.includes(action.persoonId)) {
      ctx.setState(
        patch<DeelnemersStateModel>({
          selectedDeelnemersIds: append<string>([action.persoonId]),
        }),
      );
    }
  }

  @Action(DeelnemersActions.RemoveSelectedDeelnemersId)
  removeSelectedDeelnemersId(
    ctx: StateContext<DeelnemersStateModel>,
    action: DeelnemersActions.RemoveSelectedDeelnemersId,
  ) {
    const state = ctx.getState();
    const ids = state.selectedDeelnemersIds;
    if (ids.includes(action.persoonId)) {
      ctx.setState(
        patch<DeelnemersStateModel>({
          selectedDeelnemersIds: removeItem<string>((item) => item === action.persoonId),
        }),
      );
    }
  }

  @Action(DeelnemersActions.SetSelectedDeelnemersIds)
  setSelectedDeelnemersIds(ctx: StateContext<DeelnemersStateModel>, action: DeelnemersActions.SetSelectedDeelnemersIds) {
    ctx.patchState({ selectedDeelnemersIds: [...action.persoonIds] });
  }

  @Action(DeelnemersActions.RemoveAllSelectedDeelnemersIds)
  removeAllSelectedDeelnemersIds(ctx: StateContext<DeelnemersStateModel>) {
    ctx.patchState({ selectedDeelnemersIds: [] });
  }

  @Action(DeelnemersActions.UpdatePlanningen)
  updatePlanningen(ctx: StateContext<DeelnemersStateModel>, action: DeelnemersActions.UpdatePlanningen) {
    const state = ctx.getState();
    ctx.patchState({ updatingPlanningen: true, loaded: false });

    const selectedOnderwijsLocatieId = this.store.selectSnapshot(OnderwijsLocatiesSelectors.selectedOnderwijsLocatieId);

    return this.apiDstService.updatePlanningen(action.planningen, selectedOnderwijsLocatieId, action.inhaalToets).pipe(
      tap((result) => {
        const planningen = result as PlanningDTO[];

        planningen.forEach((planning: PlanningDTO) => {
          const index = state.deelnemers.findIndex((d) => d.persoonId === planning.persoonId);

          if (index > -1) {
            const existing = state.deelnemers[index];
            const planList = existing.planning ?? [];
            const pIdx = planList.findIndex((p) => p.planningId === planning.planningId);
            const newPlanList =
              pIdx > -1 ? planList.map((p, i) => (i === pIdx ? planning : p)) : [...planList, planning];

            ctx.setState(
              patch<DeelnemersStateModel>({
                deelnemers: updateItem<DeelnemerI>(index, { ...existing, planning: newPlanList as PlanningI[] }),
              }),
            );
          }
        });
        // Signal that the deelnemers are updated to the planning-list component by setting loaded to true
        ctx.patchState({ updatingPlanningen: false, loaded: true });
        this.store.dispatch(new DeelnemersActions.LoadDeelnemersWithStartcodes(selectedOnderwijsLocatieId));
        this.store.dispatch(
          new UiActions.toasterShowSuccess({
            summary: 'Planningen',
            message: this.translate.instant('TOASTER_MESSAGES.PLANNING_UPDATE_SUCCESS'),
            testKey: 'planning-opgeslagen',
          }),
        );
      }),
      catchError((err) => {
        ctx.patchState({ updatingPlanningen: false });
        // The BFF explains why a planning was refused in the ProblemDetails detail, e.g. that the
        // inplanperiode is not open. The detail is empty when there is nothing to tell the user.
        const errorMessage = err?.error?.detail || this.translate.instant('TOASTER_MESSAGES.PLANNING_UPDATE_ERROR');
        ctx.dispatch(
          new UiActions.toasterShowError({
            summary: 'Planningen',
            message: errorMessage,
          }),
        );
        return throwError(() => err);
      }),
    );
  }

  @Action(DeelnemersActions.LoadDeelnemersWithStartcodes)
  loadDeelnemersWithStartcodes(ctx: StateContext<DeelnemersStateModel>, action: DeelnemersActions.LoadDeelnemersWithStartcodes) {
    const state = ctx.getState();

    const deelnemers$: Observable<DeelnemerDTO[]> = this.apiDstService.getDeelnemersComplete(action.OnderwijsLocatieId);

    return deelnemers$.pipe(
      tap((deelnemers) => {
        const existing = [...state.deelnemers];
        deelnemers.forEach((deelnemer) => {
          const index = existing.findIndex((d) => d.persoonId === deelnemer.persoonId && d.onderwijslocatieId === action.OnderwijsLocatieId);
          const newDeelnemer = { ...deelnemer } as DeelnemerI;
          newDeelnemer.onderwijslocatieId = action.OnderwijsLocatieId;
          newDeelnemer.antwoordblad = null;
          newDeelnemer.rapport = null;

          // The v1.1 response embeds startcode as StartcodeByProductMap (Record<product, StartcodeDTO>)
          // Pick the entry matching the current productKey filter
          const productKeys = state.startcodeProductKeys;
          const embedded = deelnemer.startcode;
          if (embedded && productKeys?.length) {
            const matchKey = productKeys.find(k => k in embedded);
            newDeelnemer.startcode = matchKey ? (embedded[matchKey] as StartcodeI) ?? null : null;
          } else {
            newDeelnemer.startcode = embedded ? (Object.values(embedded)[0] as StartcodeI) ?? null : null;
          }

          if (index > -1) {
            existing[index] = newDeelnemer;
          } else {
            existing.push(newDeelnemer);
          }
        });
        ctx.patchState({ deelnemers: existing });
      }),
      catchError((err) => {
        const errorMessage = this.translate.instant('TOASTER_MESSAGES.DEELNEMER_LOAD_ERROR', { error: err.status });
        return ctx.dispatch(
          new UiActions.toasterShowError({ summary: '', message: errorMessage }),
        );
      }),
    );
  }

  @Action(DeelnemersActions.CreateStartcodes)
  createStartcodes(ctx: StateContext<DeelnemersStateModel>, action: DeelnemersActions.CreateStartcodes) {
    const state = ctx.getState();
    ctx.patchState({ creatingStartcodes: true, loaded: false });
    const selectedOnderwijsLocatieId = this.store.selectSnapshot(OnderwijsLocatiesSelectors.selectedOnderwijsLocatieId);

    const create$: Observable<unknown> = this.apiDstService.createStartcodes(selectedOnderwijsLocatieId, action.productKeys).pipe(
      tap((grouped) => {
        const startcodes = flattenStartCodeByProduct(grouped, action.productKeys);
        this.applyStartCodes(state, ctx, startcodes);
      }),
    );

    return create$.pipe(
      tap(() => this.store.dispatch(new UiActions.toasterShowSuccess({ summary: 'Startcodes', message: this.translate.instant('TOASTER_MESSAGES.STARTCODES_CREATE_SUCCESS'), testKey: 'startcodes-aangemaakt' }))),
      catchError((err) => {
        ctx.patchState({ ...state, creatingStartcodes: false });
        return this.store.dispatch(new UiActions.toasterShowError({ summary: 'Startcodes', message: this.translate.instant('TOASTER_MESSAGES.STARTCODES_CREATE_ERROR', { error: err.status }) }));
      }),
    );
  }

  @Action(DeelnemersActions.CreateStartcodesForDeelnemers)
  createStartcodesForDeelnemers(
    ctx: StateContext<DeelnemersStateModel>,
    action: DeelnemersActions.CreateStartcodesForDeelnemers,
  ) {
    const state = ctx.getState();
    ctx.patchState({ creatingStartcodes: true, loaded: false });
    const selectedOnderwijsLocatieId = this.store.selectSnapshot(OnderwijsLocatiesSelectors.selectedOnderwijsLocatieId);

    const create$: Observable<unknown> = this.apiDstService.createStartcodesForDeelnemers(selectedOnderwijsLocatieId, action.deelnemerIds, action.productKeys).pipe(
      tap((grouped) => {
        const startcodes = flattenStartCodeByProduct(grouped, action.productKeys);
        // Partial update: only the selected deelnemers received codes, keep the rest intact.
        this.applyStartCodes(state, ctx, startcodes, false);
      }),
    );

    return create$.pipe(
      tap(() => {
        if (!action.suppressToast) {
          this.store.dispatch(new UiActions.toasterShowSuccess({ summary: 'Startcodes', message: this.translate.instant('TOASTER_MESSAGES.STARTCODES_CREATE_SUCCESS'), testKey: 'startcodes-aangemaakt' }));
        }
      }),
      catchError((err) => {
        ctx.patchState({ ...state, creatingStartcodes: false });
        return this.store.dispatch(new UiActions.toasterShowError({ summary: 'Startcodes', message: this.translate.instant('TOASTER_MESSAGES.STARTCODES_CREATE_ERROR', { error: err.status }) }));
      }),
    );
  }

  @Action(DeelnemersActions.CreateStartcodesForDeelnemersOnIds)
  createStartcodesForDeelnemersOnIds(
    ctx: StateContext<DeelnemersStateModel>,
    action: DeelnemersActions.CreateStartcodesForDeelnemersOnIds,
  ) {
    const state = ctx.getState();
    ctx.patchState({ creatingStartcodes: true, loaded: false });
    const selectedOnderwijsLocatieId = this.store.selectSnapshot(OnderwijsLocatiesSelectors.selectedOnderwijsLocatieId);

    const create$: Observable<unknown> = this.apiDstService.createStartcodesForDeelnemerIds(selectedOnderwijsLocatieId, action.persoonIds, action.productKeys).pipe(
      tap((grouped) => {
        const startcodes = flattenStartCodeByProduct(grouped);
        this.applyStartCodes(state, ctx, startcodes);
      }),
    );

    return create$.pipe(
      tap(() => this.store.dispatch(new UiActions.toasterShowSuccess({ summary: 'Startcodes', message: this.translate.instant('TOASTER_MESSAGES.STARTCODES_CREATE_SUCCESS'), testKey: 'startcodes-aangemaakt' }))),
      catchError((err) => {
        ctx.patchState({ ...state, creatingStartcodes: false });
        return this.store.dispatch(new UiActions.toasterShowError({ summary: 'Startcodes', message: this.translate.instant('TOASTER_MESSAGES.STARTCODES_CREATE_ERROR', { error: err.status }) }));
      }),
    );
  }

  @Action(DeelnemersActions.DeleteStartcodes)
  deleteStartcodes(ctx: StateContext<DeelnemersStateModel>) {
    const state = ctx.getState();
    const selectedOnderwijsLocatieId = this.store.selectSnapshot(OnderwijsLocatiesSelectors.selectedOnderwijsLocatieId);
    const deelnemers = state.deelnemers.filter(
      (deelnemer) => deelnemer.onderwijslocatieId === selectedOnderwijsLocatieId,
    );
    const startcodes: string[] = [];

    deelnemers.forEach((deelnemer) => {
      if (deelnemer.startcode?.startcode) {
        startcodes.push(deelnemer.startcode.startcode);
      }
    });

    return this.apiDstService.deleteStartcodes(selectedOnderwijsLocatieId, startcodes).pipe(
      mergeMap(() =>
        ctx.dispatch(new DeelnemersActions.LoadDeelnemers(selectedOnderwijsLocatieId, true)),
      ),
      catchError((err) =>
        this.store.dispatch(
          new UiActions.toasterShowError({
            summary: 'Startcodes',
            message: this.translate.instant('TOASTER_MESSAGES.STARTCODES_DELETE_ERROR', { error: err.status }),
          }),
        ),
      ),
    );
  }

  @Action(DeelnemersActions.ReadDeelnemerStartcodes)
  readDeelnemerStartcodes(ctx: StateContext<DeelnemersStateModel>, action: DeelnemersActions.ReadDeelnemerStartcodes) {
    const state = ctx.getState();
    const selectedOnderwijsLocatieId = this.store.selectSnapshot(OnderwijsLocatiesSelectors.selectedOnderwijsLocatieId);

    // Always use v1.1 startcodes endpoint — it supports productKeys filtering
    return this.apiDstService.readStartcodes(selectedOnderwijsLocatieId, action.productKeys).pipe(
      tap((grouped) => {
        if (!grouped) {
          ctx.patchState({ startcodesByProduct: {}, loading: false });
          return;
        }
        const startcodes = flattenStartCodeByProduct(grouped, action.productKeys);
        this.applyStartCodes(state, ctx, startcodes);
        ctx.patchState({ startcodesByProduct: grouped });
      }),
      catchError((err) => {
        ctx.patchState({ loading: false });
        return this.store.dispatch(
          new UiActions.toasterShowError({
            summary: 'Startcodes',
            message: this.translate.instant('TOASTER_MESSAGES.STARTCODES_LOAD_ERROR', { error: err.status }),
          }),
        );
      }),
    );
  }

  @Action(DeelnemersActions.PrintStartcodes)
  printStartcodes(ctx: StateContext<DeelnemersStateModel>) {
    ctx.patchState({ printStartcodes: true });
  }

  @Action(DeelnemersActions.PrintStartcodesDone)
  printStartcodesDone(ctx: StateContext<DeelnemersStateModel>) {
    ctx.patchState({ printStartcodes: false });
  }

  @Action(DeelnemersActions.SelectGroep)
  selectGroep(ctx: StateContext<DeelnemersStateModel>, action: DeelnemersActions.SelectGroep) {
    ctx.patchState({ selectedGroep: action.payload });
  }

  private readonly applyStartCodes = (state: DeelnemersStateModel, ctx: StateContext<DeelnemersStateModel>, startcodes: StartcodeDTO[], clearExisting = true) => {
    // Build the new deelnemers array in one pass and patch the state once.
    // Doing a setState per startcode would emit ~N state updates, each triggering a full rebuild
    // of the table in the view — O(n²) work that is very slow for large schools.
    // First occurrence wins. flattenStartCodeByProduct emits codes in productKeys order, so a
    // deelnemer with codes in several requested products resolves to the first key — matching the
    // v1.1 embedded path (productKeys.find) and keeping selection deterministic.
    const startcodeByPersoonId = new Map<string, StartcodeDTO>();
    for (const s of startcodes) {
      if (!startcodeByPersoonId.has(s.persoonId)) {startcodeByPersoonId.set(s.persoonId, s);}
    }
    // When clearExisting is true the incoming set covers every deelnemer, so stale codes are wiped.
    // For partial updates (e.g. startcodes created for only a few selected deelnemers) we keep the
    // existing codes of the deelnemers that are not part of this batch.
    const updated = state.deelnemers.map((d) => {
      const startcode = startcodeByPersoonId.get(d.persoonId);
      if (startcode) {return { ...d, startcode };}
      return clearExisting ? { ...d, startcode: null } : d;
    });
    ctx.patchState({ deelnemers: updated, creatingStartcodes: false, loading: false, loaded: true });
  }
}
